/**
 * SyncedField — bir MobX store'unu `SyncedPreferences` alt-mesajına bağlayan
 * TAM çift-yönlü katman (kaynak-referans `features/user/state/SyncedField.ts` 1-1 portu).
 *
 * kaynak-referanstan TEK uyarlama: remote kaynak/push hedefi kaynak-referansta `UserSettings`
 * store'u; TEBHOS'ta merkezî blob yöneticisi `SyncedPreferencesEngine`
 * (getSubPreference/setSubPreference/isHydrated). Hesap-değişimi gözlemi de
 * kaynak-referans `AuthSession.userId` yerine engine'in `currentUserId`'sinden okunur
 * (READY payload'ı kimliği engine'e ulaştırır) — semantik aynı: kullanıcı
 * değişip alan sunucuda YOKSA store defaults'a sıfırlanır (tercih sızmaz).
 *
 * Davranış (kaynak-referans ile birebir):
 *   - remote-gözlem reaction'ı: blob'daki alt-mesaj değişince karşılaştır →
 *     farklıysa applyMessage (runInAction) → push bastırılır (suppressPush).
 *   - local-gözlem reaction'ı (opsiyonel debounceMs): store değişince
 *     toMessage → eşitlik/boyut/roundtrip denetimleri → setSubPreference.
 *   - Roundtrip kararsızlığı / hata → bağ 'suspended' (störe dokunulmaz,
 *     makePersistent JSON yolu yaşamaya devam eder).
 *   - Alan sunucuda YOKSA (undefined) remote "yok" sayılır → local itilir =
 *     localStorage-migrasyonunun kendisi (bkz. Engine dosya başı notu).
 */

import { create, equals, type Message, type MessageInitShape, toBinary } from "@bufbuild/protobuf";
import type { GenMessage } from "@bufbuild/protobuf/codegenv2";
import { comparer, reaction, runInAction } from "mobx";
import { Logger } from "@app/features/platform/utils/Logger";
import { verifyRoundtripStability } from "@app/features/user/state/SyncedFieldRoundtrip";
import {
  createSyncedFieldMachineSnapshot,
  type SyncedFieldCommand,
  type SyncedFieldFailureReason,
  type SyncedFieldMachineEvent,
  selectSyncedFieldMachineModel,
  transitionSyncedFieldMachineSnapshot,
} from "@app/features/user/state/SyncedFieldStateMachine";
import type { SyncedPreferences } from "@app/features/user/state/SyncedPreferencesEngine";

export { verifyRoundtripStability };

const logger = new Logger("SyncedField");

export type SyncedPreferencesField = Exclude<keyof SyncedPreferences, "$typeName" | "$unknown" | "sanitizeUrls">;

function isMessageSchema(value: unknown): value is GenMessage<Message> {
  return (
    value != null &&
    typeof value === "object" &&
    typeof (value as { typeName?: unknown }).typeName === "string" &&
    Array.isArray((value as { fields?: unknown }).fields)
  );
}

export interface SyncedFieldConfig<
  T extends object,
  F extends SyncedPreferencesField,
  M extends Message & NonNullable<SyncedPreferences[F]>,
> {
  readonly field: F;
  readonly schema: GenMessage<M>;
  readonly persist: ReadonlyArray<keyof T>;
  toMessage(store: T): MessageInitShape<GenMessage<M>>;
  applyMessage(store: T, message: M): void;
  readonly version?: number;
  readonly debounceMs?: number;
  readonly syncAcrossTabs?: boolean;
  readonly maxEncodedBytes?: number;
  enabled?(): boolean;
  mergeRemote?(local: M, incoming: M): MessageInitShape<GenMessage<M>>;
}

export async function makeSyncedField<
  T extends object,
  F extends SyncedPreferencesField,
  M extends Message & NonNullable<SyncedPreferences[F]>,
>(store: T, config: SyncedFieldConfig<T, F, M>): Promise<void> {
  const tag = `${store.constructor.name}.${String(config.field)}`;
  const schema = config.schema;
  if (!isMessageSchema(schema)) {
    logger.error(`${tag}: geçersiz protobuf şeması; senkron kalıcılık devre dışı.`);
    return;
  }
  const isEnabled = (): boolean => config.enabled?.() ?? true;
  let machine = createSyncedFieldMachineSnapshot();
  let ownerUserId: string | null = null;
  let observedUserId: string | null = null;
  const suspend = (reason: SyncedFieldFailureReason, message: string, error?: unknown): void => {
    logger.error(`${tag}: ${message}; senkron kalıcılık devre dışı:`, error);
    transitionMachine({
      type: "sync.failed",
      failure: {
        reason,
        message,
        error,
      },
    });
  };
  const buildMessage = (): M => create(schema, config.toMessage(store)) as NonNullable<SyncedPreferences[F]> as M;
  const buildMessageOrSuspend = (message: string): M | null => {
    if (!selectSyncedFieldMachineModel(machine).isActive) return null;
    try {
      return buildMessage();
    } catch (error) {
      suspend("build-local", message, error);
      return null;
    }
  };
  const getRemoteSnapshot = (): SyncedPreferences[F] | undefined => {
    if (!selectSyncedFieldMachineModel(machine).isActive) return undefined;
    if (!isEnabled()) return undefined;
    try {
      return Engine.getSubPreference(config.field);
    } catch (error) {
      suspend("read-remote", "remote anlık görüntüsü okunamadı", error);
      return undefined;
    }
  };
  const compareRemote = (snapshot: M): void => {
    const candidate = buildMessageOrSuspend("remote güncellemesi uygulanmadan önce yerel anlık görüntü kurulamadı");
    if (candidate == null) return;
    try {
      transitionOnly({ type: equals(schema, snapshot, candidate) ? "sync.remoteMatched" : "sync.remoteNeedsApply" });
    } catch (error) {
      suspend("compare-remote", "remote anlık görüntüsü yerel durumla karşılaştırılamadı", error);
    }
  };
  const applyRemote = (snapshot: M): void => {
    let effective = snapshot;
    if (config.mergeRemote && (ownerUserId === null || ownerUserId === observedUserId)) {
      try {
        effective = create(schema, config.mergeRemote(buildMessage(), snapshot)) as M;
      } catch (error) {
        logger.error(`${tag}: mergeRemote fırlattı; remote anlık görüntüsü olduğu gibi uygulanıyor:`, error);
        effective = snapshot;
      }
    }
    try {
      runInAction(() => {
        config.applyMessage(store, effective);
      });
    } catch (error) {
      logger.error(`${tag}: applyMessage fırlattı:`, error);
    }
    ownerUserId = observedUserId;
    transitionOnly({ type: "sync.applyFinished" });
  };
  const preparePush = (candidate: M): void => {
    ownerUserId ??= observedUserId;
    try {
      const current = Engine.getSubPreference(config.field);
      if (current !== undefined && equals(schema, current as M, candidate)) {
        transitionOnly({ type: "sync.localAlreadySynced" });
        return;
      }
      const encodedBytes = toBinary(schema, candidate).length;
      if (encodedBytes > maxEncodedBytes) {
        logger.error(
          `${tag}: ${encodedBytes} baytlık yük, alan-başı ${maxEncodedBytes} bayt bütçesini aşıyor; push atıldı.`,
        );
        transitionOnly({ type: "sync.localAlreadySynced" });
        return;
      }
      const stability = verifyRoundtripStability<T, M>({
        schema,
        store,
        toMessage: config.toMessage,
        applyMessage: config.applyMessage,
        candidate,
      });
      if (stability.threw !== undefined) {
        suspend("roundtrip-threw", "roundtrip sondasında applyMessage fırlattı", stability.threw);
        return;
      }
      if (!stability.stable) {
        suspend(
          "roundtrip-unstable",
          "roundtrip-kararlılık denetimi başarısız (toMessage → applyMessage → toMessage farklı mesaj üretti)",
        );
        return;
      }
    } catch (error) {
      suspend("encode-local", "yerel anlık görüntü kodlanamadı ya da doğrulanamadı", error);
      return;
    }
    transitionOnly({ type: "sync.localReadyToPush" });
  };
  const push = (candidate: M): void => {
    void Engine.setSubPreference(config.field, candidate).catch((error) => {
      const status =
        error != null && typeof error === "object" && typeof (error as { status?: unknown }).status === "number"
          ? ((error as { status: number }).status as number)
          : null;
      if (status === 429) return;
      logger.warn(`${tag}: sunucuya senkronlanamadı:`, error);
    });
    transitionOnly({ type: "sync.commandHandled" });
  };
  const executeCommand = (command: SyncedFieldCommand): void => {
    switch (command.type) {
      case "compareRemote":
        compareRemote(command.snapshot as M);
        return;
      case "applyRemote":
        applyRemote(command.snapshot as M);
        return;
      case "preparePush":
        preparePush(command.candidate as M);
        return;
      case "pushLocal":
        push(command.candidate as M);
        return;
    }
  };
  const processCommands = (): void => {
    for (let i = 0; i < 8; i++) {
      const command = selectSyncedFieldMachineModel(machine).command;
      if (command == null) return;
      executeCommand(command);
    }
    suspend("machine-loop", "state-machine komut döngüsü güvenlik sınırını aştı");
  };
  function transitionOnly(event: SyncedFieldMachineEvent): void {
    machine = transitionSyncedFieldMachineSnapshot(machine, event);
  }
  function transitionMachine(event: SyncedFieldMachineEvent): void {
    transitionOnly(event);
    processCommands();
  }
  await Promise.resolve();
  const maxEncodedBytes = config.maxEncodedBytes ?? 65_536;
  const Engine = (await import("@app/features/user/state/SyncedPreferencesEngine")).SyncedPreferencesEngine;
  const applyDefaultsForUserChange = (userId: string): void => {
    try {
      runInAction(() => {
        config.applyMessage(store, create(schema) as M);
      });
    } catch (error) {
      logger.error(`${tag}: hesap değişimi sonrası defaults'a dönerken applyMessage fırlattı:`, error);
    }
    ownerUserId = userId;
  };
  try {
    reaction(
      () => ({
        snapshot: getRemoteSnapshot(),
        userId: Engine.currentUserId,
        hydrated: Engine.isHydrated(),
      }),
      ({ snapshot, userId, hydrated }) => {
        observedUserId = userId;
        if (
          snapshot === undefined &&
          hydrated &&
          userId != null &&
          ownerUserId != null &&
          ownerUserId !== userId &&
          selectSyncedFieldMachineModel(machine).isActive
        ) {
          applyDefaultsForUserChange(userId);
        }
        transitionMachine({
          type: "sync.remoteObserved",
          enabled: isEnabled(),
          snapshot,
        });
      },
      { fireImmediately: true, equals: comparer.shallow },
    );
    reaction(
      () => (isEnabled() ? buildMessageOrSuspend("gözlem için yerel anlık görüntü kurulamadı") : null),
      (candidate) => {
        transitionMachine({
          type: "sync.localObserved",
          enabled: isEnabled(),
          candidate,
        });
      },
      config.debounceMs != null && config.debounceMs > 0 ? { delay: config.debounceMs } : undefined,
    );
    // GERİYE-UYUM MİGRASYONU (TEBHOS eklentisi — kaynak-referansta YOK çünkü kaynak-referans
    // store'ları yalnız synced-blob'dan hidre olur; TEBHOS store'ları kendi
    // makePersistent JSON'undan hidre olur). Güvenli desen: "yerel doluysa ve
    // sunucu boşsa yerelden yükle-yaz" —
    //   1. SUNUCU HİDRASYONU BEKLENİR (isHydrated): READY gelmeden asla
    //      itilmez; taze cihazın kod-default'ları sunucudaki gerçek tercihi
    //      EZEMEZ (hidrasyon-öncesi push olsaydı dirty-koruması sunucu
    //      kopyasını gölgelerdi).
    //   2. Alan sunucuda VARSA (snapshot !== undefined) hiçbir şey yapılmaz —
    //      remote-reaction zaten uygular.
    //   3. Alan sunucuda YOKSA ve yerel durum salt-default DEĞİLSE (yerel
    //      "dolu") mevcut durum bir localObserved olarak makineye verilir →
    //      normal push yolu (boyut/roundtrip denetimli) sunucuya taşır.
    reaction(
      () => Engine.isHydrated() && isEnabled() && getRemoteSnapshot() === undefined,
      (shouldMigrate) => {
        if (!shouldMigrate) return;
        const candidate = buildMessageOrSuspend("migrasyon için yerel anlık görüntü kurulamadı");
        if (candidate == null) return;
        try {
          if (equals(schema, candidate, create(schema) as M)) return; // yerel "boş" — itilecek şey yok
        } catch {
          return;
        }
        transitionMachine({
          type: "sync.localObserved",
          enabled: isEnabled(),
          candidate,
        });
      },
      { fireImmediately: true },
    );
  } catch (error) {
    suspend("initialize-reactions", "reaction'lar başlatılamadı", error);
  }
}
